import React, { useEffect, useRef, useState, useContext } from 'react';
import { Tooltip as ReactTooltip } from 'react-tooltip';

import { eventReplyStatusEnum, user } from  '@/interfaces/planner';
import { BuildPath } from  '@/helpers/Paths';
import { CheckCircle, MinusCircle, PlusCircle, XCircle } from 'react-feather';

import { dateStringToHoursAndMinutes, hoursAndMinutesBetweenTwoDates } from  '@/helpers/Date';

import { PlannerContext } from '@/context/PlannerContext';

interface UserResultProps {
    user:user,
    selectedUsers:user[],
    setSelectedUsers:(users:user[]) => void,
    viewEdit?:boolean ,
    isGatedEvent?: boolean,
    isConfigurable?:ConstrainBoolean,
    isAwaiting?:boolean,
    getInvitees?: () => void,
    eventWorkflowId?:number,
    isProcessing?:boolean,
    setProccessingUsers?:() => void,
    isFull?:boolean,
    eventTimeRemaining?: number,
    isCancelled?: boolean
}

const UserResult = (
    {user, selectedUsers, setSelectedUsers, viewEdit, isGatedEvent, isConfigurable, isAwaiting=false, getInvitees, eventWorkflowId, isProcessing, setProccessingUsers, isFull, eventTimeRemaining, isCancelled}: UserResultProps
) => {

    const [touched, setTouched] = useState<boolean>(false);
    const [highlight, setHighlight] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const isMounted = useRef<boolean>(true);

    const { approveAwaiting } = useContext(PlannerContext);


    useEffect(() => {
        return () => {
            isMounted.current = false
        }
    }, [])

    useEffect(() => {

        if (touched){
            const className = !selectedUsers.find(x => x.id === user.id) ? 'removed' : 'added';

            setHighlight(className);

            setTimeout(() => {
                if (isMounted.current){
                    setHighlight("")
                    setTouched(false)
                }
            }, 1500);
        }
        
    }, [touched, selectedUsers]);

    async function processApplication(action: 'approved' | 'rejected', workflowId: number, responseId: number) {
        setIsLoading(true) 
        approveAwaiting(workflowId, responseId, action)
        .then(() => {
            if(!isGatedEvent)  {
                getInvitees();
            }
        })
        .catch(() => {
            // show toast
        })
        .finally(() => {
            setIsLoading(false);
        })
    }

    function canConfirm(){
        // if event is full you cannot attend
        
        if (isFull || isCancelled) {
            return false
        }

        if(eventTimeRemaining) {
            
            const eventDuration = hoursAndMinutesBetweenTwoDates(user.reply.endSelection, user.reply.startSelection)
            
            let eventDecimal = +eventDuration.split(':')[0]

            if(eventDuration.includes(':')) {
                eventDecimal += +eventDuration.split(':')[1]/60
            }
             

            if (eventDecimal > eventTimeRemaining) {
                return false
            }
        }
        

      return true
    }

    function gatedEventButtons(eventReplyStatus:eventReplyStatusEnum) {
        // edge cases
        if(isProcessing) {
            return <img src={`${BuildPath()}/images/userupload/rolling.svg`} alt='executing' style={{height: '12px'}}/>
        }

        else if(user.reply.eventReplyType === "declined") {
            return <></>
        }

        function procApp(applicationFunction: () => void) {
            applicationFunction();
            setProccessingUsers()
        }


        // expected logic
        switch(eventReplyStatus) {
            case eventReplyStatusEnum.PendingApproval:
                return (
                    <>
                        {canConfirm() && <button className='no-style-btn' onClick={() => { procApp(() => processApplication('approved', eventWorkflowId, user.reply.workflowResponseId))}}>
                            <CheckCircle size ={12} color={'#10b981'} />
                        </button>}
                        <button className='no-style-btn' onClick={() => procApp(() => processApplication('rejected', eventWorkflowId, user.reply.workflowResponseId))}>
                            <XCircle size={12} color={'#e02020'} style={{marginLeft:'8px'}}/>
                        </button>
                    </>
                    
                );
            case eventReplyStatusEnum.Processing:
                return  <img src={`${BuildPath()}/images/userupload/rolling.svg`} alt='executing' style={{height: '12px'}}/>
            case eventReplyStatusEnum.Attending:
            case eventReplyStatusEnum.AwaitingStatus:
                return <button className='no-style-btn' onClick={() => procApp(() => (processApplication('rejected', eventWorkflowId, user.reply.workflowResponseId)))}>
                    <MinusCircle size={12} color={'#e02020'} />
                </button>

            case eventReplyStatusEnum.NotAttending:
                return <>
                {canConfirm() && <button className='no-style-btn' onClick={() => procApp(() => processApplication('approved', eventWorkflowId, user.reply.workflowResponseId))}>
                    <PlusCircle size ={12} color={'#10b981'} />
                </button>}</>
        }
    }
    

    
    return (
        <>
            {(!isAwaiting) && user.reply && <ReactTooltip id={`user-${user.id}-tooltip`} place="top" className='thrive-tooltip event-modal-tt' >
                    <span>replied: {Intl.DateTimeFormat(undefined, {dateStyle:'short', timeStyle:'short', hourCycle:'h23'}).format(new Date(user.reply.repliedAt))}</span>
            </ReactTooltip>}
            <li key={user.id} className={highlight} data-tooltip-id={`user-${user.id}-tooltip`}>
                
                {!viewEdit 
                    ?   <button
                            className="select-user"
                            aria-label={!selectedUsers.find(x => x.id === user.id) ? `add ${user.displayName}` : `remove ${user.displayName}`}
                            onClick={() => {
                                    !selectedUsers.find(x => x.id === user.id)
                                        ? setSelectedUsers([...selectedUsers, user])
                                        : setSelectedUsers([...selectedUsers.filter(x => x.id !== user.id)]);
                                    setTouched(true)
                            }}
                        >
                            <div className='profile-container'>
                                {user.profilePicUrl 
                                    ? <img className='profile' src={user.profilePicUrl} alt="" />   
                                    : <img className="profile" src={`${BuildPath()}/images/planner/user-circle.svg`} alt="" />
                                }
                            </div>

                            <div className='content-container' style={{width: isGatedEvent ? '122px' : ''}}>
                                <h2>{user.displayName}</h2>

                                {isConfigurable 
                                    ? <span>{dateStringToHoursAndMinutes(user.reply.startSelection)} - {dateStringToHoursAndMinutes(user.reply.endSelection)} ({hoursAndMinutesBetweenTwoDates(user.reply.endSelection, user.reply.startSelection)}hrs)</span>
                                    : <span>{user.jobTitle}{user.jobTitle && user.department ? ' • ' : ''}{user.department}</span>
                                }
                            </div>   

                            <div className='controls' >
                                {!selectedUsers.find(x => x.id === user.id)
                                    ? <img className='add-remove-icon' src={`${BuildPath()}/images/planner/plus-circle.svg`} alt="" />
                                    : <img className='add-remove-icon' src={`${BuildPath()}/images/planner/minus-circle.svg`} alt="" />
                                }
                            </div>

                            {isGatedEvent && 
                                <div className='approve-reject-gated-control-wrapper' style={{display:'flex', alignItems:'center', justifyContent: 'flex-end', flex:1}}>
                                    {gatedEventButtons(user.eventReplyStatus)}
                                </div>
                            }
                        </button>

                    :   <div className="select-user" aria-label={!selectedUsers.find(x => x.id === user.id) ? `add ${user.displayName}` : `remove ${user.displayName}`} >
                            <div className='profile-container'>
                                {user.profilePicUrl 
                                    ? <img className='profile' src={user.profilePicUrl} alt="" />   
                                    : <img className="profile" src={`${BuildPath()}/images/planner/user-circle.svg`} alt="" />
                                }
                            </div>

                            <div className='content-container' style={{width: isGatedEvent ? '122px' : ''}}>
                                <h2>{user.displayName}</h2>
                                {isConfigurable 
                                    ? <span>{dateStringToHoursAndMinutes(user.reply.startSelection)} - {dateStringToHoursAndMinutes(user.reply.endSelection)} ({hoursAndMinutesBetweenTwoDates(user.reply.endSelection, user.reply.startSelection)}hrs)</span>
                                    : <span>{user.jobTitle}{user.jobTitle && user.department ? ' • ' : ''}{user.department}</span>
                                }
                            </div>   

                            {isGatedEvent && 
                                <div className='approve-reject-gated-control-wrapper' style={{display:'flex', alignItems:'center', justifyContent: 'flex-end', flex:1}}>
                                    {gatedEventButtons(user.eventReplyStatus)}
                                </div>
                            }
                        </div>
                }
            </li>
        </>
    );
};

export default UserResult;
